import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, BadgeCheck, Send } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

export default function AskQuestion() {
  const { id } = useParams();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => API.get(`/ask/${id}`).then((r) => setQ(r.data)).catch(() => setQ(false));
  useEffect(() => { load(); }, [id]);
  const answer = async () => {
    if (!user) { toast.error("Log in to answer 💗"); return; }
    if (!body.trim()) return;
    setBusy(true);
    try { await API.post(`/ask/${id}/answers`, { body }); setBody(""); toast.success("Answer posted 💗"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const like = async (aid) => { if (!user) { toast.error("Log in to like"); return; } try { await API.post(`/answers/${aid}/like`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const best = async (aid) => { try { await API.post(`/ask/${id}/best`, { answer_id: aid }); toast.success("Marked best answer 💗"); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (q === false) return <div className="max-w-xl mx-auto px-4 py-24 text-center"><h1 className="font-display text-2xl">Question not found</h1><Link to="/ask-sakhis" className="text-[#6A1B29] mt-4 inline-block">Back to Ask Sakhis →</Link></div>;
  if (!q) return <div className="min-h-[60vh] grid place-items-center text-stone-400">Loading…</div>;
  const isAuthor = user && q.user_id === user.id;
  return (
    <div className="bg-[#FAF6F1] min-h-[70vh]" data-testid="ask-question-page">
      <Seo title={`${q.body.slice(0, 60)} | Ask Sakhis`} canonicalPath={`/ask-sakhis/${id}`} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/ask-sakhis" className="text-sm text-stone-500">← Ask Sakhis</Link>
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mt-4">
          <h1 className="font-display text-xl sm:text-2xl font-semibold text-stone-900">{q.body}</h1>
          {q.image && <img src={q.image} alt="" className="rounded-xl mt-4 max-h-80 object-cover" />}
          <p className="text-xs text-stone-400 mt-3">{q.user_name}</p>
        </div>

        <h2 className="font-display text-lg font-semibold text-stone-900 mt-8">{q.answers.length} Answers</h2>
        <div className="space-y-3 mt-3" data-testid="answers-list">
          {q.answers.map((a) => (
            <div key={a.id} className={`bg-white border rounded-2xl p-4 ${a.is_best ? "border-[#6A1B29] ring-1 ring-[#6A1B29]" : "border-stone-200"}`} data-testid={`answer-${a.id}`}>
              {a.is_best && <p className="text-xs font-bold text-[#6A1B29] flex items-center gap-1 mb-1"><BadgeCheck className="w-4 h-4" /> Best Answer</p>}
              <p className="text-sm text-stone-800">{a.body}</p>
              <div className="flex items-center gap-4 mt-3 text-xs">
                {a.username ? <Link to={`/sakhi/${a.username}`} className="text-stone-500 hover:text-[#6A1B29]">{a.user_name}</Link> : <span className="text-stone-500">{a.user_name}</span>}
                <button onClick={() => like(a.id)} className="flex items-center gap-1 text-stone-500 hover:text-[#6A1B29]" data-testid={`answer-like-${a.id}`}><Heart className="w-3.5 h-3.5" /> {a.like_count}</button>
                {isAuthor && !a.is_best && <button onClick={() => best(a.id)} className="ml-auto text-[#6A1B29] font-semibold" data-testid={`answer-best-${a.id}`}>Mark best</button>}
              </div>
            </div>
          ))}
          {q.answers.length === 0 && <p className="text-sm text-stone-500 bg-white border border-stone-200 rounded-2xl p-6 text-center">Be the first to help 💗</p>}
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-4 mt-6 sticky bottom-4">
          <div className="flex items-end gap-2">
            <textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share your advice…" className="flex-1 border border-stone-300 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#6A1B29]" data-testid="answer-input" />
            <button onClick={answer} disabled={busy} className="bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="answer-submit"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
